/**
 * Eigenbau: Die Metarr-Karte der Übersicht — das einzige schwarze Feld dort.
 *
 * Die Vorlage stellt Metarr auf „Heute" als Tafel-Karte rechts oben: Rabe,
 * Name, die Zahl offener Vorschläge, der neueste Vorschlag als Satz, darunter
 * „Ansehen" und „Verwerfen". Sie erscheint nur, wenn etwas zur Entscheidung
 * liegt — eine leere Karte wäre eine Behauptung ohne Inhalt.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { api } from "@/lib/api";
import { Mikrolabel } from "@/components/entwurf/Mikrolabel";
import { RabeAvatar } from "@/components/RabeAvatar";
import { siteConfig } from "@/siteConfig";
import { fehlerMeldung } from "@/lib/haken";
import type { Meldung } from "@/lib/meldung";
import { Fehlerzeile } from "@/components/Fehlerzeile";
import { verweisKlassen } from "@/components/entwurf/Verweis";
import { cx, focusRing } from "@/lib/utils";
import { useState } from "react";

export function MetarrKarte({ className }: { className?: string }) {
  const client = useQueryClient();
  const [meldung, setzeMeldung] = useState<Meldung | null>(null);
  /** Der zweite Klick — erst danach wird verworfen. */
  const [fragtNach, setzeFragtNach] = useState(false);

  const vorschlaege = useQuery({
    queryKey: ["cubic", "metarr", "vorschlaege"],
    queryFn: () => api.metarr.vorschlaege.query(),
  });

  const offene = (vorschlaege.data ?? []).filter(
    (vorschlag) => vorschlag.zustand === "offen",
  );
  /*
   * Der erste **ist** der neueste — hier wird bewusst nicht sortiert.
   *
   * Das sah lange nach einem Griff ins Ungefähre aus. Der Dienst ordnet aber
   * mit `desc(metarrVorschlaege.id)`, und die Kennungen sind zeitgeordnet
   * (UUIDv7). Ein zweites Sortieren im Browser bräuchte einen Zeitstempel,
   * den der Vorschlag gar nicht mitliefert — es wäre also nicht genauer,
   * sondern nur schwerer nachvollziehbar.
   */
  const neuester = offene[0];

  const verwerfen = useMutation({
    mutationFn: (vorschlagId: string) =>
      api.metarr.ablehnen.mutate({ vorschlagId }),
    onSuccess: async () => {
      setzeMeldung(null);
      setzeFragtNach(false);
      await client.invalidateQueries({ queryKey: ["cubic", "metarr"] });
    },
    onError: (fehler) => setzeMeldung(fehlerMeldung(fehler)),
  });

  // Die Nullregel: keine offenen Vorschläge, keine Karte.
  if (offene.length === 0 || !neuester) return null;

  return (
    <div className={className}>
      <div className="bg-tafel px-6 pb-[26px] pt-6 text-tafel-tinte">
        <div className="flex items-center gap-2.5">
          <RabeAvatar groesse={24} />
          <Mikrolabel grad={12} ton="tafel" className="text-tafel-tinte">
            Metarr
          </Mikrolabel>
          <Mikrolabel
            grad={11}
            className="ml-auto text-tafel-tinte3"
          >
            {offene.length} neu
          </Mikrolabel>
        </div>
        <p className="mt-[18px] text-[15px] font-light leading-[1.55] text-tafel-tinte">
          {neuester.begruendung || neuester.aktion}
        </p>
        {meldung ? (
          <div className="mt-3">
            <Fehlerzeile meldung={meldung} />
          </div>
        ) : null}
        <div className="mt-[22px] flex flex-wrap items-center gap-[18px]">
          <Link
            to={siteConfig.baseLinks.einstellungen.metarr}
            className={verweisKlassen({ tafel: true })}
          >
            Ansehen
          </Link>
          {/*
            Verwerfen fragt nach — in zwei Schritten statt in einem Dialog.
            Es ist die einzige Handlung auf dieser Seite, die etwas wegnimmt,
            und sie hatte weder Rückfrage noch Rückweg noch einen sichtbaren
            Fokus. Ein Fenster wäre für eine Karte zu schwer; der zweite Klick
            reicht, und „Doch nicht" nimmt ihn zurück.
          */}
          {fragtNach ? (
            <span className="flex flex-wrap items-center gap-[18px]">
              <button
                type="button"
                onClick={() => verwerfen.mutate(neuester.id)}
                disabled={verwerfen.isPending}
                className={cx(
                  "cursor-pointer font-mono text-[12px] text-tafel-tinte underline decoration-1 underline-offset-[3px]",
                  focusRing,
                )}
              >
                {verwerfen.isPending ? "Verwerfe …" : "Wirklich verwerfen"}
              </button>
              <button
                type="button"
                onClick={() => setzeFragtNach(false)}
                className={cx(
                  "cursor-pointer font-mono text-[12px] text-tafel-tinte3 hover:text-tafel-tinte",
                  focusRing,
                )}
              >
                Doch nicht
              </button>
            </span>
          ) : (
            <button
              type="button"
              onClick={() => setzeFragtNach(true)}
              className={cx(
                "cursor-pointer font-mono text-[12px] text-tafel-tinte3 hover:text-tafel-tinte",
                focusRing,
              )}
            >
              Verwerfen
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
